import React, { useState, useEffect } from 'react';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calculator, DollarSign, Clock, CheckCircle, AlertCircle, Building2, Calendar, HelpCircle, MapPin, Users, Phone, ChevronDown, MessageCircle, Crown, Star, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API } from '../utils/authAxios';

// Tipos de tratamiento con valores actualizados 2026
const TREATMENT_TYPES = [
  {
    id: 'senda',
    name: 'GRATUITO SENDA',
    description: 'Ambulatorio o residencial financiado por el Estado',
    priceMin: 0,
    priceMax: 0,
    isResidential: false,
    color: 'emerald'
  },
  {
    id: 'online',
    name: 'Tratamiento Online',
    description: 'Sesiones virtuales, flexible y accesible',
    priceMin: 200000,
    priceMax: 350000,
    isResidential: false,
    color: 'cyan'
  },
  {
    id: 'ambulatorio',
    name: 'Ambulatorio Privado',
    description: 'Sesiones regulares sin internación',
    priceMin: 350000,
    priceMax: 600000,
    isResidential: false,
    color: 'blue'
  },
  {
    id: 'residencial_estandar',
    name: 'Residencial Estándar',
    description: 'Internación con programa completo',
    priceMin: 300000,
    priceMax: 700000,
    isResidential: true,
    color: 'purple'
  },
  {
    id: 'residencial_premium',
    name: 'Residencial Premium',
    description: 'Instalaciones de lujo, atención personalizada',
    priceMin: 800000,
    priceMax: 3000000,
    isResidential: true,
    color: 'amber'
  }
];

// Duraciones de tratamiento
const DURATIONS = [
  { id: '6', name: '6 meses', months: 6 },
  { id: '9', name: '9 meses', months: 9 },
  { id: '12', name: '12 meses', months: 12 }
];

// Duración de seguimiento
const FOLLOWUP_DURATIONS = [
  { id: '3', name: '3 meses (mínimo)', months: 3 },
  { id: '6', name: '6 meses', months: 6 },
  { id: '12', name: '12 meses (recomendado)', months: 12 }
];

// Regiones de Chile
const CHILEAN_REGIONS = [
  { id: 'metropolitana', name: 'Región Metropolitana', slug: 'santiago' },
  { id: 'valparaiso', name: 'Valparaíso', slug: 'valparaiso' },
  { id: 'biobio', name: 'Biobío', slug: 'concepcion' },
  { id: 'araucania', name: 'La Araucanía', slug: 'temuco' },
  { id: 'ohiggins', name: "O'Higgins", slug: 'rancagua' },
  { id: 'maule', name: 'Maule', slug: 'talca' },
  { id: 'coquimbo', name: 'Coquimbo', slug: 'la-serena' },
  { id: 'antofagasta', name: 'Antofagasta', slug: 'antofagasta' },
  { id: 'los-lagos', name: 'Los Lagos', slug: 'puerto-montt' },
  { id: 'tarapaca', name: 'Tarapacá', slug: 'iquique' },
  { id: 'arica', name: 'Arica y Parinacota', slug: 'arica' },
  { id: 'magallanes', name: 'Magallanes', slug: 'punta-arenas' },
  { id: 'nuble', name: 'Ñuble', slug: 'chillan' },
  { id: 'los-rios', name: 'Los Ríos', slug: 'valdivia' },
  { id: 'atacama', name: 'Atacama', slug: 'copiapo' },
  { id: 'aysen', name: 'Aysén', slug: 'coyhaique' }
];

// Datos del FAQ para SEO
const FAQ_DATA = [
  {
    question: "¿Cuánto cobran para internar a un drogadicto?",
    answer: "En Chile, internar a una persona con adicción en un centro residencial tiene un costo que varía según el tipo de centro. Los centros residenciales estándar cobran entre $300.000 y $700.000 mensuales, mientras que los centros premium van desde $800.000 hasta $3.000.000 mensuales. A esto se suman gastos adicionales como medicamentos ($15.000-$100.000), gastos personales semanales llamados 'mascota' ($10.000-$50.000), y gastos de visitas familiares. SENDA ofrece opciones gratuitas llamando al 1412. Adicionalmente, en Chile puedes solicitar una licencia médica psiquiátrica que cubre parte del periodo de tratamiento residencial."
  },
  {
    question: "¿Cuánto cuesta rehabilitar a un drogadicto?",
    answer: "El costo total de rehabilitación incluye el tratamiento activo (6-12 meses) más el seguimiento post-alta (3-12 meses). Para tratamiento residencial estándar, el costo total puede ir desde $2.400.000 hasta $8.400.000 dependiendo de la duración y el centro. El tratamiento ambulatorio cuesta desde $350.000 mensuales, y el online desde $200.000 mensuales. Es importante considerar que el seguimiento posterior es fundamental para evitar recaídas y tiene un costo adicional reducido."
  },
  {
    question: "¿Cuánto cuesta un tratamiento de rehabilitación?",
    answer: "Los costos mensuales de tratamiento de rehabilitación en Chile 2026 son: Tratamiento Online desde $200.000, Ambulatorio Privado desde $350.000, Residencial Estándar desde $300.000 hasta $700.000, y Residencial Premium desde $800.000 hasta $3.000.000. La duración recomendada es de 6 a 12 meses de tratamiento activo, más un mínimo de 3 meses de seguimiento (idealmente 12 meses). Existen opciones gratuitas a través de SENDA."
  },
  {
    question: "¿Dónde pedir ayuda para dejar las drogas en Chile?",
    answer: "En Chile puedes pedir ayuda en: 1) SENDA - Llama gratis al 1412 las 24 horas para orientación y acceso a tratamiento gratuito. 2) Tu CESFAM más cercano para evaluación inicial. 3) Centros de rehabilitación privados que ofrecen modalidades residencial, ambulatoria y online. 4) Grupos de apoyo como Alcohólicos Anónimos y Narcóticos Anónimos. 5) Profesionales especializados (psicólogos y psiquiatras en adicciones). En SinAdicciones.org puedes encontrar un directorio completo de centros y profesionales verificados."
  }
];

// Componente InfoTooltip que funciona en móvil con click/tap
function InfoTooltip({ children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center justify-center">
          <HelpCircle className="w-4 h-4 text-slate-500 hover:text-emerald-400 cursor-pointer" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-slate-800 border-slate-700 text-slate-200 text-sm max-w-xs p-3">
        {children}
      </PopoverContent>
    </Popover>
  );
}

// Centros relacionados según resultado de la calculadora — prioriza planes de pago
function PlanBadge({ planType, isPremium }) {
  if (planType === 'elite' || isPremium) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/40 text-amber-300 uppercase tracking-wide">
        <Crown className="w-3 h-3" /> Elite
      </span>
    );
  }
  if (planType === 'premium') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 uppercase tracking-wide">
        <Star className="w-3 h-3" /> Premium
      </span>
    );
  }
  if (planType === 'destacado') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 uppercase tracking-wide">
        Destacado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700/40 border border-slate-600/40 text-slate-400 uppercase tracking-wide">
      Verificado
    </span>
  );
}

function RelatedCentersCard({ modality, regionId }) {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ modality: modality || '', limit: '4' });
        if (regionId) params.set('region', regionId);
        const res = await fetch(`${API}/calculator/related-centers?${params.toString()}`);
        const data = res.ok ? await res.json() : { centers: [] };
        if (!cancelled) setCenters(data.centers || []);
      } catch {
        if (!cancelled) setCenters([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [modality, regionId]);

  if (loading || centers.length === 0) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="calc-related-centers">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-50 text-lg flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-400" />
          Centros que coinciden con tu caso
        </CardTitle>
        <p className="text-xs text-slate-500">Ordenados por nivel de respuesta. Los <span className="text-amber-400">Elite</span> y <span className="text-emerald-400">Premium</span> responden cotizaciones en minutos.</p>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {centers.map((c) => (
          <Link
            key={c.id}
            to={`/centro/${c.slug}`}
            className="block group bg-slate-950/60 hover:bg-slate-950 border border-slate-800 hover:border-emerald-500/40 rounded-lg p-3 transition-all"
            data-testid={`related-center-${c.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h4 className="text-slate-50 font-semibold text-sm leading-tight truncate group-hover:text-emerald-300">{c.name}</h4>
                  <PlanBadge planType={c.plan_type} isPremium={c.is_premium} />
                </div>
                <p className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap">
                  {c.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.city}</span>}
                  {(c.modality || []).length > 0 && (
                    <span className="text-slate-600">·</span>
                  )}
                  {(c.modality || []).slice(0, 2).map((m, i) => (
                    <span key={i} className="text-slate-500">{m}</span>
                  ))}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 shrink-0 mt-1 transition-colors" />
            </div>
            {c.description && (
              <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{c.description}</p>
            )}
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

// Opciones para el formulario de cotización (mismas que CotizacionLanding para consistencia)
const QUOTE_ADDICTIONS = ['Alcohol', 'Cocaína', 'Pasta Base', 'Marihuana', 'Benzodiacepinas / Medicamentos', 'Ludopatía', 'Adicciones conductuales', 'Patología dual', 'Otras'];
const QUOTE_URGENCY = [
  { value: 'inmediata', label: 'Inmediata' },
  { value: 'esta_semana', label: 'Esta semana' },
  { value: 'flexible', label: 'Flexible' },
];

export function TreatmentCalculator() {
  const [treatmentType, setTreatmentType] = useState('');
  const [duration, setDuration] = useState('');
  const [followUp, setFollowUp] = useState('3');
  const [region, setRegion] = useState('');
  const [includeMascota, setIncludeMascota] = useState(false);
  const [hasDualPathology, setHasDualPathology] = useState(false);
  const [includeVisits, setIncludeVisits] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Quote-from-calculator state
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [quotePhone, setQuotePhone] = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  const [quoteUrgency, setQuoteUrgency] = useState('');
  const [quoteAddiction, setQuoteAddiction] = useState('');
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteError, setQuoteError] = useState('');

  const API_URL = (process.env.REACT_APP_BACKEND_URL && typeof window !== 'undefined' && !process.env.REACT_APP_BACKEND_URL.includes(window.location.hostname) && !process.env.REACT_APP_BACKEND_URL.includes('localhost')) ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

  function validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  async function calculate() {
    if (!treatmentType || !duration) return;
    
    // Validate email
    if (!email.trim()) {
      setEmailError('Por favor ingresa tu email para ver los resultados');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Por favor ingresa un email válido');
      return;
    }
    setEmailError('');
    setIsSubmitting(true);

    // Register lead and subscribe to newsletter
    try {
      // Subscribe to newsletter
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase(),
          source: 'calculadora',
          subscriber_type: 'persona'
        })
      });

      // Also register as calculator lead for specific tracking
      const calcRes = await fetch(`${API_URL}/api/calculator/register-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase(),
          treatment_type: treatmentType,
          region: region,
          duration: duration,
          has_dual_pathology: hasDualPathology
        })
      });
      if (!calcRes.ok) {
        const errBody = await calcRes.text().catch(() => '');
        console.error(`[Calculator] register-lead failed: ${calcRes.status}`, errBody);
      }
      // GA4 dataLayer push — calculator completed (key conversion)
      try {
        const a = await import('../services/analytics');
        a.trackEvent('calculator_complete', 'calculator', treatmentType);
      } catch (e) { /* noop */ }
    } catch (error) {
      console.error('Error registering lead:', error);
      // Continue anyway to show results
    }

    setIsSubmitting(false);

    const treatment = TREATMENT_TYPES.find(t => t.id === treatmentType);
    const durationData = DURATIONS.find(d => d.id === duration);
    const followUpData = FOLLOWUP_DURATIONS.find(f => f.id === followUp);
    const regionData = CHILEAN_REGIONS.find(r => r.id === region);

    if (!treatment || !durationData) return;

    // Calcular costo base mensual (mínimo y máximo)
    const baseMonthlyMin = treatment.priceMin;
    const baseMonthlyMax = treatment.priceMax;

    // Gastos adicionales
    let extrasMin = 0;
    let extrasMax = 0;

    // Medicamentos (patología dual)
    if (hasDualPathology) {
      extrasMin += 15000;
      extrasMax += 100000;
    } else {
      // Medicamentos básicos
      extrasMin += 10000;
      extrasMax += 30000;
    }

    // Mascota (solo residencial) - semanal, convertir a mensual (4 semanas)
    if (treatment.isResidential && includeMascota) {
      extrasMin += 10000 * 4; // 40.000/mes
      extrasMax += 50000 * 4; // 200.000/mes
    }

    // Gastos de visita (solo residencial)
    if (treatment.isResidential && includeVisits) {
      extrasMin += 30000; // 30.000/mes (pasajes, comida)
      extrasMax += 100000; // 100.000/mes
    }

    // Pasajes (ambulatorio)
    if (treatment.id === 'ambulatorio') {
      extrasMin += 30000;
      extrasMax += 60000;
    }

    // Calcular totales mensuales
    const monthlyMin = baseMonthlyMin + extrasMin;
    const monthlyMax = baseMonthlyMax + extrasMax;

    // Total tratamiento
    const totalTreatmentMin = monthlyMin * durationData.months;
    const totalTreatmentMax = monthlyMax * durationData.months;

    // Seguimiento (costo reducido, 30% a 50% del mensual para residencial)
    const followUpMultiplierMin = treatment.isResidential ? 0.3 : 0.3;
    const followUpMultiplierMax = treatment.isResidential ? 0.5 : 0.3;
    const followUpMonthlyMin = Math.round(monthlyMin * followUpMultiplierMin);
    const followUpMonthlyMax = Math.round(monthlyMax * followUpMultiplierMax);
    const totalFollowUpMin = followUpMonthlyMin * (followUpData?.months || 3);
    const totalFollowUpMax = followUpMonthlyMax * (followUpData?.months || 3);

    // Gran total
    const grandTotalMin = totalTreatmentMin + totalFollowUpMin;
    const grandTotalMax = totalTreatmentMax + totalFollowUpMax;

    setResult({
      treatment,
      durationData,
      followUpData: followUpData || { months: 3 },
      regionData,
      monthlyMin,
      monthlyMax,
      extrasMin,
      extrasMax,
      totalTreatmentMin,
      totalTreatmentMax,
      followUpMonthlyMin,
      followUpMonthlyMax,
      totalFollowUpMin,
      totalFollowUpMax,
      grandTotalMin,
      grandTotalMax,
      includeMascota,
      hasDualPathology,
      includeVisits
    });
    setShowResult(true);
  }

  function formatMoney(amount) {
    if (amount === 0) return 'Gratis';
    return '$' + amount.toLocaleString('es-CL');
  }

  function reset() {
    setTreatmentType('');
    setDuration('');
    setFollowUp('3');
    setRegion('');
    setIncludeMascota(false);
    setHasDualPathology(false);
    setIncludeVisits(false);
    setShowResult(false);
    setResult(null);
    setQuoteOpen(false);
    setQuoteName('');
    setQuotePhone('');
    setQuoteMessage('');
    setQuoteUrgency('');
    setQuoteAddiction('');
    setQuoteResult(null);
    setQuoteError('');
  }

  // Map calculator treatmentType → cotizador modality
  function mapModality(t) {
    if (t === 'residencial_estandar' || t === 'residencial_premium') return 'residencial';
    if (t === 'ambulatorio') return 'ambulatorio';
    if (t === 'online') return 'online';
    return '';
  }

  async function submitQuote() {
    setQuoteError('');
    if (!quoteName.trim() || quoteName.trim().length < 2) {
      setQuoteError('Por favor ingresa tu nombre completo');
      return;
    }
    const phoneClean = quotePhone.replace(/\s+/g, '');
    if (!phoneClean || phoneClean.length < 8) {
      setQuoteError('Por favor ingresa un teléfono o WhatsApp válido');
      return;
    }
    setQuoteSubmitting(true);
    try {
      const payload = {
        target_type: 'center',
        name: quoteName.trim(),
        phone: phoneClean,
        email: email.toLowerCase(),
        region: result?.regionData?.name || '',
        modality: mapModality(treatmentType),
        addiction: quoteAddiction || undefined,
        urgency: quoteUrgency || undefined,
        message: quoteMessage.trim() || undefined,
        source: 'calculadora',
      };
      const res = await fetch(`${API_URL}/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Error al enviar la cotización');
      }
      const data = await res.json();
      setQuoteResult(data);
    } catch (err) {
      console.error('quote submit error', err);
      setQuoteError('No pudimos enviar la cotización. Intenta nuevamente en unos minutos.');
    } finally {
      setQuoteSubmitting(false);
    }
  }

  const selectedTreatment = TREATMENT_TYPES.find(t => t.id === treatmentType);

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Calculadora de Costos de Rehabilitación de Drogas Chile 2026 | SinAdicciones</title>
        <meta name="description" content="Calcula el costo real de un tratamiento de rehabilitación de drogas en Chile. Precios actualizados 2026: residencial desde $300.000, ambulatorio desde $350.000, online desde $200.000." />
        <meta name="keywords" content="cuanto cuesta rehabilitación drogas chile, precio internar drogadicto, costo tratamiento adicciones, calculadora rehabilitación" />
        <link rel="canonical" href="https://sinadicciones.org/calculadora" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/calculadora" />
        <meta property="og:title" content="Calculadora de Costos de Rehabilitación de Drogas Chile 2026" />
        <meta property="og:description" content="Calcula el costo real de un tratamiento de rehabilitación en Chile. Residencial, ambulatorio u online — recibe estimación al instante." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:secure_url" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:image:alt" content="Calculadora de costos de rehabilitación - SinAdicciones.org" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Calculadora de Costos de Rehabilitación Chile 2026" />
        <meta name="twitter:description" content="Calcula el costo de un tratamiento de rehabilitación en Chile. Estimación instantánea, gratuita." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        
        {/* FAQ Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_DATA.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}</script>
      
      
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Hero */}
          <div className="grid md:grid-cols-5 gap-8 items-center mb-10">
            <div className="md:col-span-3 text-center md:text-left">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 mb-5">
                <Calculator className="w-7 h-7 text-emerald-400" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-50 mb-4 leading-tight">
                Calculadora de costos de <span className="text-emerald-400">rehabilitación</span>
              </h1>
              <p className="text-slate-300 text-base md:text-lg mb-4 leading-relaxed">
                Estima el costo real de un tratamiento de adicciones en Chile 2026 — residencial, ambulatorio, online o SENDA gratuito. Valores actualizados a precios reales del mercado.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 border border-slate-700 text-xs text-slate-300">
                  <CheckCircle className="w-3 h-3 text-emerald-400" /> Gratis
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 border border-slate-700 text-xs text-slate-300">
                  <CheckCircle className="w-3 h-3 text-emerald-400" /> Sin registro
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 border border-slate-700 text-xs text-slate-300">
                  <CheckCircle className="w-3 h-3 text-emerald-400" /> Valores reales 2026
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 border border-slate-700 text-xs text-slate-300">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" /> Cotización opcional
                </span>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="relative max-w-[360px] mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 blur-3xl rounded-full" />
                <img
                  src={`${API.replace(/\/api$/, '')}/api/calculator/hero-image`}
                  alt="Ilustración del costo de tratamiento de rehabilitación en Chile"
                  className="relative w-full h-auto rounded-2xl shadow-2xl ring-1 ring-emerald-500/10"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  loading="lazy"
                  data-testid="calc-hero-image"
                />
              </div>
            </div>
          </div>

          {/* SEO intro copy (para posicionar la página) */}
          <div className="mb-10 p-5 rounded-xl bg-slate-900/50 border border-slate-800 text-sm text-slate-400 leading-relaxed">
            <p className="mb-2">
              <strong className="text-slate-200">¿Cuánto cuesta rehabilitar a un drogadicto en Chile?</strong> Depende de la modalidad y la duración. En 2026, un tratamiento <strong className="text-slate-300">residencial estándar</strong> cuesta entre <span className="text-emerald-400 font-semibold">$300.000 y $700.000 CLP/mes</span>, un <strong className="text-slate-300">ambulatorio</strong> entre <span className="text-emerald-400 font-semibold">$350.000 y $700.000/mes</span>, y un <strong className="text-slate-300">tratamiento online</strong> entre <span className="text-emerald-400 font-semibold">$200.000 y $500.000/mes</span>. SENDA ofrece atención <span className="text-emerald-400 font-semibold">gratuita</span> llamando al 1412.
            </p>
            <p>
              Usa la calculadora para obtener una estimación personalizada en menos de 30 segundos. Considera duración del tratamiento (6 a 12 meses), seguimiento post-alta y extras opcionales como medicamentos y visitas familiares. Si tienes <strong className="text-slate-300">ISAPRE o FONASA</strong>, puedes solicitar <strong className="text-slate-300">licencia médica psiquiátrica</strong> para cubrir parte del periodo.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
              {/* Calculator Form */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                    Configura tu tratamiento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Treatment Type */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Tipo de tratamiento</Label>
                    <Select value={treatmentType} onValueChange={(v) => { setTreatmentType(v); setIncludeMascota(false); setIncludeVisits(false); }}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="treatment-type-select">
                        <SelectValue placeholder="Selecciona tipo de tratamiento" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {TREATMENT_TYPES.map(type => (
                          <SelectItem key={type.id} value={type.id} className="text-slate-300 focus:bg-slate-800">
                            <div>
                              <p className="font-medium">{type.name}</p>
                              <p className="text-xs text-slate-500">
                                {type.priceMin === 0 ? 'Gratuito' : `${formatMoney(type.priceMin)} - ${formatMoney(type.priceMax)}/mes`}
                              </p>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Region */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Tu región</Label>
                    <Select value={region} onValueChange={setRegion}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="region-select">
                        <SelectValue placeholder="Selecciona tu región" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                        {CHILEAN_REGIONS.map(r => (
                          <SelectItem key={r.id} value={r.id} className="text-slate-300 focus:bg-slate-800">
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Duration */}
                  <div>
                    <Label className="text-slate-300 mb-2 block">Duración del tratamiento</Label>
                    <Select value={duration} onValueChange={setDuration}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="duration-select">
                        <SelectValue placeholder="Selecciona duración" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {DURATIONS.map(d => (
                          <SelectItem key={d.id} value={d.id} className="text-slate-300 focus:bg-slate-800">
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Follow-up Duration */}
                  <div>
                    <Label className="text-slate-300 mb-2 block flex items-center gap-2">
                      Duración del seguimiento
                      <InfoTooltip>
                        <p>El seguimiento post-alta es fundamental para prevenir recaídas. Puede ser online o semi-presencial.</p>
                      </InfoTooltip>
                    </Label>
                    <Select value={followUp} onValueChange={setFollowUp}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200">
                        <SelectValue placeholder="Selecciona duración" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {FOLLOWUP_DURATIONS.map(f => (
                          <SelectItem key={f.id} value={f.id} className="text-slate-300 focus:bg-slate-800">
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Additional Options */}
                  <div className="space-y-4 pt-4 border-t border-slate-800">
                    <Label className="text-slate-300 block">Gastos adicionales opcionales</Label>
                    
                    {/* Patología Dual */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="dual-pathology"
                        checked={hasDualPathology}
                        onChange={(e) => setHasDualPathology(e.target.checked)}
                        className="w-4 h-4 accent-emerald-500 rounded"
                      />
                      <label htmlFor="dual-pathology" className="text-slate-300 text-sm flex items-center gap-2 cursor-pointer">
                        Patología dual (medicamentos adicionales)
                        <InfoTooltip>
                          <p><strong>Patología dual</strong> es cuando existe una adicción junto con otro trastorno mental (depresión, ansiedad, bipolaridad, etc.). Requiere medicación psiquiátrica adicional que puede costar entre $15.000 y $100.000 mensuales.</p>
                        </InfoTooltip>
                      </label>
                    </div>

                    {/* Mascota (solo residencial) */}
                    {selectedTreatment?.isResidential && (
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="mascota"
                          checked={includeMascota}
                          onChange={(e) => setIncludeMascota(e.target.checked)}
                          className="w-4 h-4 accent-emerald-500 rounded"
                        />
                        <label htmlFor="mascota" className="text-slate-300 text-sm flex items-center gap-2 cursor-pointer">
                          Incluir &quot;mascota&quot; semanal
                          <InfoTooltip>
                            <p><strong>&quot;Mascota&quot;</strong> es el dinero semanal que la familia entrega al centro para que el paciente pueda comprar artículos personales: shampoo, chocolates, cigarros, snacks, revistas, etc. Generalmente cuesta entre $10.000 y $50.000 semanales según lo que pida el usuario.</p>
                          </InfoTooltip>
                        </label>
                      </div>
                    )}

                    {/* Gastos de visita (solo residencial) */}
                    {selectedTreatment?.isResidential && (
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="visits"
                          checked={includeVisits}
                          onChange={(e) => setIncludeVisits(e.target.checked)}
                          className="w-4 h-4 accent-emerald-500 rounded"
                        />
                        <label htmlFor="visits" className="text-slate-300 text-sm flex items-center gap-2 cursor-pointer">
                          Incluir gastos de visita (fines de semana)
                          <InfoTooltip>
                            <p>Gastos de traslado y alimentación para visitar al paciente los fines de semana. Varía según la distancia al centro.</p>
                          </InfoTooltip>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Email field for lead capture */}
                  <div className="pt-4 border-t border-slate-800">
                    <Label className="text-slate-300 mb-2 block">
                      Tu email <span className="text-emerald-400">*</span>
                    </Label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      data-testid="email-input"
                    />
                    {emailError && (
                      <p className="text-red-400 text-sm mt-1">{emailError}</p>
                    )}
                    <p className="text-slate-500 text-xs mt-2">
                      Te enviaremos información útil sobre tratamientos y ofertas especiales. Sin spam.
                    </p>
                  </div>

                  {/* Calculate Button */}
                  <Button 
                    onClick={calculate} 
                    disabled={!treatmentType || !duration || isSubmitting}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 py-6 text-lg disabled:opacity-50"
                    data-testid="calculate-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Calculando...
                      </>
                    ) : (
                      <>
                        <Calculator className="w-5 h-5 mr-2" />
                        Calcular Costo
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results */}
              <div className="space-y-6">
                {!showResult ? (
                  <Card className="bg-slate-900 border-slate-800 h-full flex items-center justify-center min-h-[400px]">
                    <CardContent className="text-center py-16">
                      <Calculator className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                      <p className="text-slate-500">Selecciona las opciones y presiona calcular para ver los resultados</p>
                    </CardContent>
                  </Card>
                ) : result && (
                  <>
                    {/* Main Result */}
                    <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <p className="text-slate-400 mb-2">Costo Total Estimado</p>
                          {result.treatment.id === 'senda' ? (
                            <p className="text-4xl md:text-5xl font-bold text-emerald-400" data-testid="total-cost">
                              GRATIS
                            </p>
                          ) : (
                            <>
                              <p className="text-3xl md:text-4xl font-bold text-emerald-400" data-testid="total-cost">
                                {formatMoney(result.grandTotalMin)} - {formatMoney(result.grandTotalMax)}
                              </p>
                            </>
                          )}
                          <p className="text-sm text-slate-500 mt-2">
                            {result.durationData.months} meses tratamiento + {result.followUpData.months} meses seguimiento
                          </p>
                        </div>

                        {result.treatment.id !== 'senda' && (
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                              <p className="text-lg font-bold text-slate-50">
                                {formatMoney(result.monthlyMin)} - {formatMoney(result.monthlyMax)}
                              </p>
                              <p className="text-sm text-slate-400">Mensual (con extras)</p>
                            </div>
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                              <p className="text-lg font-bold text-slate-50">
                                {formatMoney(result.followUpMonthlyMin)} - {formatMoney(result.followUpMonthlyMax)}
                              </p>
                              <p className="text-sm text-slate-400">Seguimiento/mes</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Quote-from-calculator (sube arriba: principal CTA tras el cálculo) */}
                    {result.treatment.id !== 'senda' && (
                      <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/40" data-testid="calc-quote-card">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-slate-50 text-lg flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-emerald-400" />
                            Recibe cotizaciones reales de centros — gratis
                          </CardTitle>
                          <p className="text-sm text-slate-400">Te conectamos con centros que se ajustan a tu modalidad ({result.treatment.name}) y región. Sin compromiso.</p>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {quoteResult ? (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-5 text-center" data-testid="calc-quote-success">
                              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                              <p className="text-slate-50 font-semibold mb-1">¡Cotización enviada!</p>
                              <p className="text-slate-300 text-sm">
                                {quoteResult.matched_count > 0
                                  ? `Notificamos a ${quoteResult.matched_count} centro${quoteResult.matched_count === 1 ? '' : 's'} que se ajustan a tu caso.`
                                  : 'Recibimos tu solicitud. Te contactaremos cuando un centro coincida con tu zona y modalidad.'}
                              </p>
                              <p className="text-slate-500 text-xs mt-3">Revisa tu email <span className="text-emerald-400">{email}</span> en los próximos minutos.</p>
                            </div>
                          ) : !quoteOpen ? (
                            <Button
                              onClick={() => setQuoteOpen(true)}
                              className="w-full bg-emerald-500 hover:bg-emerald-600 py-5 text-base"
                              data-testid="calc-quote-toggle"
                            >
                              <MessageCircle className="w-5 h-5 mr-2" />
                              Pedir cotización gratis
                            </Button>
                          ) : (
                            <div className="space-y-4">
                              {/* Datos pre-llenados */}
                              <div className="flex flex-wrap gap-2 pb-1">
                                <span className="px-3 py-1 bg-slate-950 border border-slate-700 rounded-full text-xs text-slate-300">
                                  <span className="text-slate-500">Modalidad:</span> {result.treatment.name}
                                </span>
                                {result.regionData && (
                                  <span className="px-3 py-1 bg-slate-950 border border-slate-700 rounded-full text-xs text-slate-300">
                                    <span className="text-slate-500">Región:</span> {result.regionData.name}
                                  </span>
                                )}
                                <span className="px-3 py-1 bg-slate-950 border border-slate-700 rounded-full text-xs text-slate-300">
                                  <span className="text-slate-500">Email:</span> {email}
                                </span>
                              </div>

                              <div>
                                <Label className="text-slate-300 mb-1.5 block text-sm">Tu nombre completo <span className="text-emerald-400">*</span></Label>
                                <input
                                  type="text"
                                  value={quoteName}
                                  onChange={(e) => { setQuoteName(e.target.value); setQuoteError(''); }}
                                  placeholder="Ej: María Pérez"
                                  className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  data-testid="calc-quote-name"
                                />
                              </div>

                              <div>
                                <Label className="text-slate-300 mb-1.5 block text-sm">Teléfono / WhatsApp <span className="text-emerald-400">*</span></Label>
                                <input
                                  type="tel"
                                  value={quotePhone}
                                  onChange={(e) => { setQuotePhone(e.target.value); setQuoteError(''); }}
                                  placeholder="+56 9 1234 5678"
                                  className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  data-testid="calc-quote-phone"
                                />
                              </div>

                              <div>
                                <Label className="text-slate-300 mb-1.5 block text-sm">Sustancia o tipo de adicción</Label>
                                <select
                                  value={quoteAddiction}
                                  onChange={(e) => setQuoteAddiction(e.target.value)}
                                  className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  data-testid="calc-quote-addiction"
                                >
                                  <option value="">Selecciona una opción (opcional)</option>
                                  {QUOTE_ADDICTIONS.map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <Label className="text-slate-300 mb-1.5 block text-sm">Urgencia para iniciar</Label>
                                <div className="grid grid-cols-3 gap-2">
                                  {QUOTE_URGENCY.map((u) => {
                                    const active = quoteUrgency === u.value;
                                    return (
                                      <button
                                        key={u.value}
                                        type="button"
                                        onClick={() => setQuoteUrgency(active ? '' : u.value)}
                                        className={`p-2.5 rounded-lg border text-xs font-medium transition ${active ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600'}`}
                                        data-testid={`calc-quote-urgency-${u.value}`}
                                      >
                                        {u.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              <div>
                                <Label className="text-slate-300 mb-1.5 block text-sm">Mensaje (opcional)</Label>
                                <textarea
                                  value={quoteMessage}
                                  onChange={(e) => setQuoteMessage(e.target.value)}
                                  placeholder="Cuéntanos brevemente tu situación o lo que estás buscando"
                                  rows={3}
                                  className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                                  data-testid="calc-quote-message"
                                />
                              </div>

                              {quoteError && (
                                <p className="text-red-400 text-sm" data-testid="calc-quote-error">{quoteError}</p>
                              )}

                              <Button
                                onClick={submitQuote}
                                disabled={quoteSubmitting}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 py-5 text-base disabled:opacity-50"
                                data-testid="calc-quote-submit"
                              >
                                {quoteSubmitting ? (
                                  <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Enviando…
                                  </>
                                ) : (
                                  <>
                                    <MessageCircle className="w-5 h-5 mr-2" />
                                    Enviar cotización a centros
                                  </>
                                )}
                              </Button>

                              <p className="text-slate-500 text-[11px] text-center leading-relaxed">
                                Compartimos tu nombre, teléfono y email solo con los centros que coinciden con tu modalidad y región. No mostramos tu cálculo de costos.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Breakdown */}
                    {result.treatment.id !== 'senda' && (
                      <Card className="bg-slate-900 border-slate-800">
                        <CardHeader>
                          <CardTitle className="text-slate-50 text-lg">Desglose de costos</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-slate-800">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Tratamiento ({result.durationData.months} meses)</span>
                            </div>
                            <span className="text-slate-50 font-medium text-sm">
                              {formatMoney(result.totalTreatmentMin)} - {formatMoney(result.totalTreatmentMax)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-slate-800">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Seguimiento ({result.followUpData.months} meses)</span>
                            </div>
                            <span className="text-slate-50 font-medium text-sm">
                              {formatMoney(result.totalFollowUpMin)} - {formatMoney(result.totalFollowUpMax)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 text-sm">
                            <span className="text-slate-400">Extras incluidos (medicamentos{result.includeMascota ? ', mascota' : ''}{result.includeVisits ? ', visitas' : ''})</span>
                            <span className="text-slate-400">
                              {formatMoney(result.extrasMin)} - {formatMoney(result.extrasMax)}/mes
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Recommendations */}
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-slate-50 mb-4 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          Recomendaciones
                        </h3>
                        <ul className="space-y-2 text-sm text-slate-400">
                          {result.treatment.id === 'senda' ? (
                            <>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                Llama al 1412 para solicitar cupo gratuito
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                Acércate a tu CESFAM para evaluación inicial
                              </li>
                            </>
                          ) : (
                            <>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span><strong>ISAPRE / FONASA:</strong> consulta si tu plan cubre psiquiatría, hospitalización psiquiátrica o reembolso parcial del tratamiento.</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span><strong>Licencia médica psiquiátrica:</strong> en Chile es un derecho. Tu psiquiatra puede extenderla para cubrir el periodo de internación o tratamiento intensivo, asegurando que tu empleador mantenga tu sueldo durante la rehabilitación.</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                Pregunta por planes de pago en cuotas o financiamiento con tarjeta.
                              </li>
                            </>
                          )}
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            Verifica que el centro tenga autorización sanitaria
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            Visita las instalaciones antes de ingresar
                          </li>
                        </ul>
                        
                        {/* Info about follow-up cost */}
                        {result.treatment.isResidential && (
                          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                            <p className="text-sm text-blue-300">
                              <strong>Sobre el seguimiento:</strong> El costo del seguimiento post-alta generalmente representa entre un 30% y 50% del valor mensual del tratamiento residencial. Es fundamental para prevenir recaídas.
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Button variant="outline" onClick={reset} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                          Calcular de nuevo
                        </Button>
                        <Button asChild className="bg-emerald-500 hover:bg-emerald-600">
                          <Link to={result.regionData ? `/ciudad/centro-rehabilitacion-${result.regionData.slug}` : '/centros-de-rehabilitacion-drogas-chile'}>
                            <Building2 className="w-4 h-4 mr-2" />
                            Ver centros
                          </Link>
                        </Button>
                      </div>
                      
                      {/* Region-specific links */}
                      {result.regionData && (
                        <div className="grid grid-cols-2 gap-3">
                          <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                            <Link to={`/profesionales?city=${result.regionData.slug}`}>
                              <Users className="w-4 h-4 mr-2" />
                              Profesionales en {result.regionData.name.split(' ')[0]}
                            </Link>
                          </Button>
                          <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                            <Link to="/centros-de-rehabilitacion-de-drogas-gratis">
                              <DollarSign className="w-4 h-4 mr-2" />
                              Opciones gratuitas
                            </Link>
                          </Button>
                        </div>
                      )}

                      {/* CTA — Ayuda para familias (cuando lo busca un familiar) */}
                      <Link
                        to="/como-ayudar-a-un-familiar-con-adiccion"
                        data-testid="calc-familiar-link"
                        className="block rounded-lg border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 hover:border-emerald-400/60 transition p-4"
                      >
                        <div className="text-xs text-emerald-300 font-semibold uppercase tracking-wider mb-1">¿Buscas ayuda para un familiar?</div>
                        <div className="text-sm text-slate-200">Test de 7 preguntas + guía para hablarle sin que se cierre →</div>
                      </Link>

                      {/* Related centers (paid plans first) */}
                      <RelatedCentersCard
                        modality={result.treatment.id}
                        regionId={region}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

          {/* Info Section */}
          <div className="mt-16">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-50 mb-6">¿Cómo funciona esta calculadora?</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                      <span className="text-emerald-400 font-bold">1</span>
                    </div>
                    <h3 className="font-semibold text-slate-50 mb-2">Selecciona tipo de tratamiento</h3>
                    <p className="text-sm text-slate-400">
                      Desde gratuito SENDA hasta residencial premium. Cada opción tiene diferentes costos y beneficios.
                    </p>
                  </div>
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                      <span className="text-emerald-400 font-bold">2</span>
                    </div>
                    <h3 className="font-semibold text-slate-50 mb-2">Configura gastos adicionales</h3>
                    <p className="text-sm text-slate-400">
                      Agrega gastos opcionales como medicamentos, &quot;mascota&quot; semanal y visitas para un cálculo más preciso.
                    </p>
                  </div>
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                      <span className="text-emerald-400 font-bold">3</span>
                    </div>
                    <h3 className="font-semibold text-slate-50 mb-2">Obtén tu estimación</h3>
                    <p className="text-sm text-slate-400">
                      Incluye rango de costos (mínimo-máximo), tratamiento activo y seguimiento post-alta.
                    </p>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium">Importante</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Esta calculadora proporciona estimaciones basadas en precios de mercado chileno 2026. 
                        Los costos reales varían según el centro, ubicación y necesidades específicas. 
                        Siempre solicita un presupuesto detallado antes de tomar una decisión.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section for SEO */}
          <div className="mt-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50 mb-2 text-center">
              Preguntas Frecuentes sobre Costos de Rehabilitación
            </h2>
            <p className="text-slate-400 text-center mb-8">
              Resolvemos las dudas más buscadas en Google sobre tratamiento de adicciones en Chile
            </p>
            
            <div className="space-y-3 max-w-3xl mx-auto">
              {FAQ_DATA.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div 
                    key={index} 
                    className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-800/50 transition-colors"
                      data-testid={`faq-question-${index}`}
                    >
                      <span className="font-medium text-slate-200">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-emerald-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Final */}
          <div className="mt-16">
            <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
              <CardContent className="p-8 text-center">
                <Phone className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-50 mb-2">¿Necesitas ayuda inmediata?</h3>
                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                  Llama gratis al 1412 (SENDA) las 24 horas para orientación y acceso a tratamiento gratuito en Chile.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                    <a href="tel:1412">
                      <Phone className="w-5 h-5 mr-2" />
                      Llamar al 1412
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to="/centros-de-rehabilitacion-drogas-chile">
                      <Building2 className="w-5 h-5 mr-2" />
                      Ver todos los centros
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
